/**
 * Server-side template detection.
 * Reads configuration from Cloudflare Worker–injected window.__TEMPLATE_CONFIG__
 * (shape: templateName, province, detectionMethod, etc.). Cookie fallback uses
 * Worker cookie names: user_country, template_type, user_language, user_province.
 */

/** ISO2 en mayúsculas o "UNKNOWN". Evita que `country === "AR"` falle si el Worker manda "ar". */
export function normalizeWorkerCountry(raw: unknown): string {
  if (raw === undefined || raw === null) return "UNKNOWN";
  const s = String(raw).trim();
  if (!s) return "UNKNOWN";
  const up = s.toUpperCase();
  if (up === "UNKNOWN") return "UNKNOWN";
  if (/^[A-Z]{2}$/.test(up)) return up;
  const two = up.slice(0, 2);
  if (/^[A-Z]{2}$/.test(two)) return two;
  return "UNKNOWN";
}

function normalizeWorkerLanguage(raw: unknown, countryCode: string): string {
  const s = String(raw ?? "").trim().replace(/_/g, "-");
  if (!s) return countryCode === "AR" ? "es-AR" : "en-US";
  return s;
}

export interface ServerTemplateConfig {
  country: string;
  templateName: string;
  bioLayout: string | null;
  language: string;
  province: string | null;
  /** Worker: iso_region_code | region_text | gps | city | … */
  provinceDetectionMethod?: string | null;
  cfCountry: string;
  timezone: string;
  city: string;
  detectionMethod: string;
  /** WhatsApp broadcast button for Argentina provinces (CABA, Buenos Aires, Santa Fe, etc.) */
  whatsappBroadcast?: boolean;
  /** Wishlist CTA: true solo fuera de Argentina (Worker; en AR nunca wishlist) */
  showWishlist?: boolean;
  /** Hide all CTA buttons for this province (AR_NO_CTA_PROVINCES in Worker) */
  hideCta?: boolean;
  /** CTA comunidad → /invitacion-premium (Worker: x-ar-cta / DB) */
  showCommunity?: boolean;
  /** CTA cumpleaños (Worker) */
  showBirthday?: boolean;
  /** CTA dual video → /video-landing (Worker: cta_type=dual_video en wishlist_geo_config) */
  showDualVideo?: boolean;
  /** Worker: city_ar | province_ar | country | country_ar | country_default */
  ctaResolvedFrom?: string;
  /** Worker: geo_code que matcheó (ej. quilmes, Buenos Aires, US) */
  ctaResolvedMatched?: string;
  /** True si la IP parece pertenecer a un VPN/datacenter/cloud (cf.asOrganization) */
  vpnDetected?: boolean;
  /** Nombre de la organización ASN detectada por Cloudflare */
  asOrganization?: string;
  /** Edge privacy gate (geo_sim anonymous/hosting). Clean never sets this. */
  privacyBlocked?: boolean;
  privacyIsAnonymous?: boolean;
}

/**
 * Get template configuration from server-injected window object
 * The Cloudflare Worker injects this before the page loads
 */
export function getServerTemplateConfig(): ServerTemplateConfig | null {
  if (typeof window === "undefined") return null;

  // Primary source: injected by Cloudflare Worker
  const serverConfig = window.__TEMPLATE_CONFIG__;

  if (serverConfig) {
    const country = normalizeWorkerCountry(serverConfig.country);
    return {
      country,
      templateName: serverConfig.templateName || "BaseTemplate",
      bioLayout: serverConfig.bioLayout || null,
      language: normalizeWorkerLanguage(serverConfig.language, country),
      province: serverConfig.province || null,
      provinceDetectionMethod:
        typeof (serverConfig as { provinceDetectionMethod?: string })
          .provinceDetectionMethod === "string"
          ? (serverConfig as { provinceDetectionMethod?: string })
              .provinceDetectionMethod
          : null,
      cfCountry: normalizeWorkerCountry(
        serverConfig.cfCountry ?? serverConfig.country,
      ),
      timezone: serverConfig.timezone || "",
      city: serverConfig.city || "",
      detectionMethod: serverConfig.detectionMethod || "cloudflare_geo",
      whatsappBroadcast: !!serverConfig.whatsappBroadcast,
      showWishlist: !!serverConfig.showWishlist,
      hideCta: !!serverConfig.hideCta,
      showCommunity: !!serverConfig.showCommunity,
      showBirthday: !!serverConfig.showBirthday,
      showDualVideo: !!serverConfig.showDualVideo,
      ctaResolvedFrom:
        typeof serverConfig.ctaResolvedFrom === "string"
          ? serverConfig.ctaResolvedFrom
          : undefined,
      ctaResolvedMatched:
        typeof serverConfig.ctaResolvedMatched === "string"
          ? serverConfig.ctaResolvedMatched
          : undefined,
      vpnDetected: serverConfig.vpnDetected === true,
      asOrganization: typeof serverConfig.asOrganization === "string" ? serverConfig.asOrganization : undefined,
      privacyBlocked: serverConfig.privacyBlocked === true,
      privacyIsAnonymous: serverConfig.privacyIsAnonymous === true,
    };
  }

  // Fallback: try to read from cookies (set by Worker)
  const cookies = document.cookie.split(";").reduce(
    (acc, cookie) => {
      const [key, value] = cookie.trim().split("=");
      acc[key] = value;
      return acc;
    },
    {} as Record<string, string>,
  );

  if (cookies.user_country) {
    let countryNorm = "";
    try {
      countryNorm = decodeURIComponent(cookies.user_country)
        .trim()
        .toUpperCase()
        .slice(0, 2);
    } catch {
      countryNorm = cookies.user_country.trim().toUpperCase().slice(0, 2);
    }
    // WhatsApp broadcast: only for AR with qualifying provinces
    const arProvinces = ["CABA", "Buenos Aires", "Santa Fe", "Entre Ríos", "Río Negro", "San Luis", "La Pampa"];
    const province = cookies.user_province || null;
    const whatsappBroadcast =
      countryNorm === "AR" && province && arProvinces.includes(province);

    const rawLang = cookies.user_language?.replace(/_/g, "-") || "";
    const langFromCookie =
      countryNorm === "AR"
        ? rawLang.toLowerCase().startsWith("es")
          ? rawLang
          : "es-AR"
        : rawLang || "en-US";

    // CTA cookies (Worker sets show_community / hide_cta / show_birthday, TTL 1h)
    const flag = (k: string) => {
      const v = (cookies[k] || "").trim();
      return v === "1" || v === "true";
    };
    return {
      country: countryNorm || normalizeWorkerCountry(cookies.user_country),
      templateName: cookies.template_type || "BaseTemplate",
      bioLayout: null,
      language: normalizeWorkerLanguage(langFromCookie, countryNorm || "UNKNOWN"),
      province,
      cfCountry: normalizeWorkerCountry(cookies.user_country),
      timezone: "",
      city: "",
      detectionMethod: "cookies_fallback",
      whatsappBroadcast,
      showWishlist: false, // wishlist no-AR; Worker re-evaluates on full inject
      hideCta: flag("hide_cta"),
      showCommunity: flag("show_community"),
      showBirthday: flag("show_birthday"),
    };
  }

  return null;
}
